/**
 * Kiosk Ayarları > "Kategoriler" + "Ürünler" tab'larındaki seçimler için
 * şirket geneli, isimli localStorage taslakları.
 *
 * Pattern, hızlı satış'taki "Depo/Stok Taslağı" sisteminin (bkz.
 * `gts-web/src/components/salesSystem/quickSale/utils/warehouseStockTemplates.ts`)
 * birebir kiosk uyarlamasıdır. Birden fazla kiosk arasında menü kategorileri
 * ve ürünleri tek tıkla aktarmak için kullanılır.
 *
 * Anahtar: `kioskCategoryProductTemplates_${companyId}`
 * Değer:   JSON.stringify(KioskCategoryProductTemplate[])
 *
 * Notlar:
 *  - Kiosk-bağımsızdır: aynı şirketin tüm kiosk'larından erişilebilir.
 *  - Resim URL'leri pass-through; kaynak kiosk'un Firebase storage path'i
 *    şablonda kalır. Firebase URL public olduğundan farklı kiosk'tan
 *    görüntülenebilir. Eski kiosk silinip resimleri kaldırılırsa bu
 *    referanslar broken olur (kabul edilen risk).
 *  - JSON corruption'a karşı `sanitizeTemplate` koruması var; bozuk değer
 *    geldiğinde kayıt atlanır, uygulama crash olmaz.
 */

import type { KioskCategory } from '../components/KioskCategoriesTab';
import type { KioskProduct } from '../components/KioskProductsTab';

export interface KioskCategoryProductTemplate {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  categories: KioskCategory[];
  kioskProducts: { [categoryId: string]: KioskProduct[] };
}

const KEY_PREFIX = 'kioskCategoryProductTemplates';

export function getTemplatesKey(
  companyId: string | null | undefined
): string | null {
  if (!companyId) return null;
  return `${KEY_PREFIX}_${companyId}`;
}

function generateTemplateId(): string {
  // Tarayıcı uyumlu, çakışmaya dirençli kısa id.
  return `tpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function sanitizeKioskCategory(raw: any): KioskCategory | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = typeof raw.id === 'string' ? raw.id : '';
  const name = typeof raw.name === 'string' ? raw.name : '';
  if (!id || !name) return null;
  const order = typeof raw.order === 'number' ? raw.order : 0;
  const imageUrl = typeof raw.imageUrl === 'string' ? raw.imageUrl : undefined;
  const showRelatedProductsForAll =
    typeof raw.showRelatedProductsForAll === 'boolean'
      ? raw.showRelatedProductsForAll
      : undefined;
  return {
    id,
    name,
    order,
    ...(imageUrl !== undefined ? { imageUrl } : {}),
    ...(showRelatedProductsForAll !== undefined
      ? { showRelatedProductsForAll }
      : {}),
  };
}

function sanitizeKioskProduct(raw: any): KioskProduct | null {
  if (!raw || typeof raw !== 'object') return null;
  const kioskProductId =
    typeof raw.kioskProductId === 'string' ? raw.kioskProductId : '';
  if (!kioskProductId) return null;
  const order = typeof raw.order === 'number' ? raw.order : 0;
  const relatedProductIds = Array.isArray(raw.relatedProductIds)
    ? raw.relatedProductIds.filter((x: unknown) => typeof x === 'string')
    : undefined;
  const customizationOptions = Array.isArray(raw.customizationOptions)
    ? raw.customizationOptions.filter((x: unknown) => typeof x === 'string')
    : undefined;
  return {
    kioskProductId,
    order,
    ...(relatedProductIds !== undefined ? { relatedProductIds } : {}),
    ...(customizationOptions !== undefined ? { customizationOptions } : {}),
  };
}

function sanitizeTemplate(raw: any): KioskCategoryProductTemplate | null {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (!name) return null;

  const id = typeof raw.id === 'string' && raw.id ? raw.id : generateTemplateId();
  const createdAt = typeof raw.createdAt === 'number' ? raw.createdAt : Date.now();
  const updatedAt = typeof raw.updatedAt === 'number' ? raw.updatedAt : createdAt;

  const categories = Array.isArray(raw.categories)
    ? raw.categories
        .map(sanitizeKioskCategory)
        .filter((c: KioskCategory | null): c is KioskCategory => c !== null)
    : [];

  const kioskProducts: { [categoryId: string]: KioskProduct[] } = {};
  if (raw.kioskProducts && typeof raw.kioskProducts === 'object') {
    for (const [catId, list] of Object.entries(raw.kioskProducts)) {
      if (!Array.isArray(list)) continue;
      const cleaned = list
        .map(sanitizeKioskProduct)
        .filter((p): p is KioskProduct => p !== null);
      if (cleaned.length > 0) {
        kioskProducts[catId] = cleaned;
      }
    }
  }

  return {
    id,
    name,
    createdAt,
    updatedAt,
    categories,
    kioskProducts,
  };
}

export function loadKioskCategoryProductTemplates(
  companyId: string | null | undefined
): KioskCategoryProductTemplate[] {
  const key = getTemplatesKey(companyId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeTemplate)
      .filter(
        (t): t is KioskCategoryProductTemplate => t !== null
      )
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (e) {
    console.warn('[kioskCategoryProductTemplates] load parse hatası:', e);
    return [];
  }
}

function persistTemplates(
  companyId: string | null | undefined,
  templates: KioskCategoryProductTemplate[]
): boolean {
  const key = getTemplatesKey(companyId);
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify(templates));
    return true;
  } catch (e) {
    console.warn('[kioskCategoryProductTemplates] persist hatası:', e);
    return false;
  }
}

export interface SaveKioskTemplateInput {
  name: string;
  categories: KioskCategory[];
  kioskProducts: { [categoryId: string]: KioskProduct[] };
}

/**
 * Yeni taslak oluşturur. Aynı isimde taslak varsa üzerine yazar (silent
 * overwrite — referans `warehouseStockTemplates` ile aynı davranış).
 * Döner: oluşturulan/güncellenen taslak veya null.
 */
export function saveKioskCategoryProductTemplate(
  companyId: string | null | undefined,
  input: SaveKioskTemplateInput
): KioskCategoryProductTemplate | null {
  if (!companyId) return null;
  const cleanName = (input.name || '').trim();
  if (!cleanName) return null;

  const existing = loadKioskCategoryProductTemplates(companyId);
  const now = Date.now();

  const duplicateIdx = existing.findIndex(
    (t) => t.name.trim().toLowerCase() === cleanName.toLowerCase()
  );

  let updatedTemplate: KioskCategoryProductTemplate;
  if (duplicateIdx >= 0) {
    updatedTemplate = {
      ...existing[duplicateIdx],
      name: cleanName,
      updatedAt: now,
      categories: input.categories,
      kioskProducts: input.kioskProducts,
    };
    existing[duplicateIdx] = updatedTemplate;
  } else {
    updatedTemplate = {
      id: generateTemplateId(),
      name: cleanName,
      createdAt: now,
      updatedAt: now,
      categories: input.categories,
      kioskProducts: input.kioskProducts,
    };
    existing.unshift(updatedTemplate);
  }

  const ok = persistTemplates(companyId, existing);
  if (!ok) return null;
  console.log(
    `📦 [kioskCategoryProductTemplates] kaydedildi: "${cleanName}" (id=${updatedTemplate.id})`
  );
  return updatedTemplate;
}

export function deleteKioskCategoryProductTemplate(
  companyId: string | null | undefined,
  templateId: string
): boolean {
  if (!companyId || !templateId) return false;
  const existing = loadKioskCategoryProductTemplates(companyId);
  const filtered = existing.filter((t) => t.id !== templateId);
  if (filtered.length === existing.length) return false;
  const ok = persistTemplates(companyId, filtered);
  if (ok) {
    console.log(
      `📦 [kioskCategoryProductTemplates] silindi: id=${templateId}`
    );
  }
  return ok;
}
